import React, { useMemo, useState } from 'react';
import { isoToLocalInput } from '../utils/datetime';
// 행사 시작·시간·종료 양방향 계산 — 영상 상세/업로드/비로그인 업로드와 **같은 규칙**.
import { durationFromEnd, endFromDuration, humanizeMinutes, isRangeInvalid, onStartChanged, snapOnStartBlur } from '../utils/eventDuration';

/**
 * 편성 문진 모달 (QUICKUI Stage 2+3) — 두 입구(달력 드롭 / 비로그인 업로드 승인)가 공유.
 * 문진 응답이 배치를 결정한다(요일 박스·Public·상시 슬롯은 서버 applyRecipe 가 자동).
 * 제출 콜백은 부모가 주입하고, 응답 영수증(placements/warnings)을 결과 화면으로 보여준다.
 * 어휘는 도메인 중립("행사" — 공연 특정 표현 금지).
 */

export type PlacementMode = 'box' | 'slot' | 'none';

export interface RecipeValues {
  /** datetime-local. 빈 문자열 = 값 없음. box 배치는 필수(요일 파생). */
  eventStartAt: string;
  /** 행사 종료(097) — **홍보 종료의 기본값**. 비면 서버가 종전대로 행사 시작으로 폴백. */
  eventEndAt: string;
  promoStartAt: string;
  promoEndAt: string;
  /** box=영상 박스(이전 행사 만료 후 이어서) / slot=빈 슬롯 바로(홍보 일정대로,
   *  없으면 자동 추가) / none=업로드만 */
  placement: PlacementMode;
  isPublic: boolean;
}

export interface ReceiptPlacement {
  kind: 'event_start' | 'weekday_box' | 'public_box' | 'direct_slot' | 'expiry' | 'publish_start';
  slotAdded?: boolean;
  boxName?: string;
  setName?: string;
  slotNumber?: number;
  startAt?: string;
  endAt?: string | null;
  at?: string | null;
}

export interface ReceiptData {
  placements: ReceiptPlacement[];
  warnings: string[];
}

interface Props {
  title: string;
  /** 달력 드롭 진입 시 드롭한 날짜(YYYY-MM-DD) — 행사 일시의 날짜부 프리필. */
  dropDateKey?: string;
  /** 이전 레시피/대기 항목 기반 프리필(ISO 문자열). */
  prefill?: {
    eventStartAt?: string | null;
    eventEndAt?: string | null;
    promoStartAt?: string | null;
    promoEndAt?: string | null;
    placement?: PlacementMode | null;
    isPublic?: boolean;
  } | null;
  /** 승인 모드 안내(배치는 문진이 결정 — 표시용). */
  approveMode?: boolean;
  onSubmit: (values: RecipeValues) => Promise<{ ok: boolean; receipt?: ReceiptData; error?: string }>;
  onClose: () => void;
}

const HOUR_MS = 3600 * 1000;
const WEEK_MS = 7 * 24 * HOUR_MS;
const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

function receiptSentences(receipt: ReceiptData): string[] {
  const fmt = (v?: string | null) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '';
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const lines: string[] = [];
  for (const p of receipt.placements) {
    if (p.kind === 'event_start') lines.push(`행사 시각 ${fmt(p.at)} — 화면에 카운트다운이 붙습니다.`);
    else if (p.kind === 'weekday_box') lines.push(`'${p.boxName}' 박스에 ${fmt(p.startAt)}부터 ${fmt(p.endAt)}까지 노출됩니다.`);
    else if (p.kind === 'public_box') lines.push(`'${p.boxName}' 박스(다른 운영처)에도 ${fmt(p.startAt)}~${fmt(p.endAt)} 노출됩니다.`);
    else if (p.kind === 'direct_slot') lines.push(
      p.slotAdded
        ? `빈 슬롯이 없어 ${p.setName} 세트에 ${p.slotNumber}번 슬롯을 추가해 배정했습니다.`
        : `${p.setName} 세트 ${p.slotNumber}번 슬롯에 배정됐습니다.`);
    else if (p.kind === 'expiry') lines.push(`${fmt(p.at)}에 노출이 끝납니다.`);
    else if (p.kind === 'publish_start') lines.push(`${fmt(p.at)}부터 노출이 시작됩니다 — 그 전에는 슬롯에 있어도 재생되지 않습니다.`);
  }
  return lines;
}

const RecipeInterview: React.FC<Props> = ({ title, dropDateKey, prefill, approveMode, onSubmit, onClose }) => {
  const initial = useMemo<RecipeValues>(() => {
    // 행사 일시: 이전 값 > 드롭 날짜(이전 값의 시:분 유지, 없으면 19:00).
    let eventLocal = '';
    if (dropDateKey) {
      const prevTime = prefill?.eventStartAt ? isoToLocalInput(prefill.eventStartAt).slice(11) : '19:00';
      eventLocal = `${dropDateKey}T${prevTime || '19:00'}`;
    } else if (prefill?.eventStartAt) {
      eventLocal = isoToLocalInput(prefill.eventStartAt);
    }
    const eventMs = eventLocal ? new Date(eventLocal).getTime() : NaN;
    // 홍보 시작/종료: 이전 값 우선, 없으면 행사 −7일 / +1시간 (QUICKUI 문진 매핑).
    const promoStart = prefill?.promoStartAt
      ? isoToLocalInput(prefill.promoStartAt)
      : (isNaN(eventMs) ? '' : isoToLocalInput(new Date(eventMs - WEEK_MS)));
    // 행사 종료: 이전 값 > 행사 시작 +1시간(기본 행사 시간).
    const eventEnd = prefill?.eventEndAt
      ? isoToLocalInput(prefill.eventEndAt)
      : (isNaN(eventMs) ? '' : isoToLocalInput(new Date(eventMs + HOUR_MS)));
    // 홍보 종료: 이전 값 > **행사 종료**(097 규약). 종전엔 행사 시작 +1시간을 직접 썼는데,
    // 이제 그 값은 행사 종료가 들고 있고 홍보 종료는 그걸 따라간다.
    const promoEnd = prefill?.promoEndAt ? isoToLocalInput(prefill.promoEndAt) : eventEnd;
    return {
      eventStartAt: eventLocal,
      eventEndAt: eventEnd,
      promoStartAt: promoStart,
      promoEndAt: promoEnd,
      // 기본 배치 = slot(2026-08-31 확정) — 종전엔 행사 일시가 있으면 box 였다.
      // 이전 레시피(prefill)가 있으면 그 선택을 존중(재적용 멱등).
      placement: prefill?.placement ?? 'slot',
      isPublic: prefill?.isPublic ?? false,
    };
  }, [dropDateKey, prefill]);

  const [values, setValues] = useState<RecipeValues>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<ReceiptData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof RecipeValues>(k: K, v: RecipeValues[K]) => setValues(prev => ({ ...prev, [k]: v }));

  // 행사 시간(분) — 저장 필드가 아니라 행사 시작·종료를 물리는 입력 보조(utils/eventDuration).
  const [durationMin, setDurationMin] = useState(() =>
    durationFromEnd(initial.eventStartAt, initial.eventEndAt));
  // 홍보 창을 사용자가 직접 만졌는가. 값 비교로 추측하면 파생원이 셋(행사 시작·종료·시간)이라
  // 금세 어긋난다 — 손댔는지는 명시적으로 기억한다.
  const [promoStartTouched, setPromoStartTouched] = useState(false);
  const [promoEndTouched, setPromoEndTouched] = useState(false);

  /** 행사 창이 바뀌면 손대지 않은 홍보 칸을 따라 옮긴다(홍보 종료 기본값 = 행사 종료). */
  const withPromoFollow = (next: RecipeValues): RecipeValues => {
    const ms = next.eventStartAt ? new Date(next.eventStartAt).getTime() : NaN;
    if (!promoStartTouched && !isNaN(ms)) next.promoStartAt = isoToLocalInput(new Date(ms - WEEK_MS));
    if (!promoEndTouched) next.promoEndAt = next.eventEndAt;
    return next;
  };

  const onEventChange = (v: string) => {
    const r = onStartChanged(v, durationMin);
    setDurationMin(r.durationMin);
    setValues(prev => withPromoFollow({ ...prev, eventStartAt: r.start, eventEndAt: r.end }));
  };

  const onDurationChange = (v: string) => {
    setDurationMin(v);
    const end = endFromDuration(values.eventStartAt, v);
    if (end === null) return;
    setValues(prev => withPromoFollow({ ...prev, eventEndAt: end }));
  };

  const onEventEndChange = (v: string) => {
    setDurationMin(durationFromEnd(values.eventStartAt, v));
    setValues(prev => withPromoFollow({ ...prev, eventEndAt: v }));
  };

  const onEventStartBlur = () => {
    const snap = snapOnStartBlur(values.eventStartAt, values.eventEndAt);
    if (!snap) return;
    setDurationMin(snap.durationMin);
    setValues(prev => withPromoFollow({ ...prev, eventEndAt: snap.end }));
  };

  /** 행사 종료가 시작보다 이르면 제출 불가. */
  const eventRangeInvalid = isRangeInvalid(values.eventStartAt, values.eventEndAt);

  const submit = async () => {
    if (values.placement === 'box' && !values.eventStartAt) {
      setError('박스 배치는 행사 일시가 필요합니다(요일 박스) — 일시를 넣거나 다른 배치를 선택하세요.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await onSubmit(values);
      if (r.ok) setReceipt(r.receipt ?? { placements: [], warnings: [] });
      else setError(r.error || '적용에 실패했습니다.');
    } catch {
      setError('적용에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const eventWeekday = values.eventStartAt ? WEEKDAY_KO[new Date(values.eventStartAt).getDay()] : null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        {receipt ? (
          <>
            <h3 className="text-lg font-bold text-gray-800 mb-3">송출 조건 적용됨</h3>
            <p className="text-sm text-gray-500 mb-3 truncate">{title}</p>
            <ul className="space-y-1 mb-3">
              {receiptSentences(receipt).map((line, i) => (
                <li key={i} className="text-sm text-gray-700">• {line}</li>
              ))}
              {receipt.placements.length === 0 && (
                <li className="text-sm text-gray-500">배치된 곳이 없습니다.</li>
              )}
            </ul>
            {receipt.warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded p-3 mb-3">
                {receipt.warnings.map((w, i) => (
                  <p key={i} className="text-sm text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={onClose} className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 text-sm">닫기</button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-lg font-bold text-gray-800 mb-1">{approveMode ? '승인 · 송출 조건' : '송출 조건'}</h3>
            <p className="text-sm text-gray-500 mb-4 truncate">{title}</p>

            {/* 배치 방식 — 업로드 영상의 세 가지 길(사용자 확정).
                순서·기본값 = '빈 슬롯에 바로 넣기'(2026-08-31 확정 — 가장 흔한 길). */}
            <div className="space-y-2 mb-4">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="radio" name="placement" className="mt-0.5" checked={values.placement === 'slot'}
                  onChange={() => set('placement', 'slot')} />
                <span>
                  빈 슬롯에 바로 넣기
                  <span className="block text-xs text-gray-400">다른 행사와 관계없이 홍보 일정대로 바로 노출 — 빈 슬롯이 없으면 자동 추가(최대 200)</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="radio" name="placement" className="mt-0.5" checked={values.placement === 'box'}
                  onChange={() => set('placement', 'box')} />
                <span>
                  영상 박스에 넣기{eventWeekday ? ` (${eventWeekday}요일 박스)` : ' (행사 요일 박스)'}
                  <span className="block text-xs text-gray-400">이전 행사 영상이 끝난 뒤 이어서 노출됩니다</span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="radio" name="placement" className="mt-0.5" checked={values.placement === 'none'}
                  onChange={() => set('placement', 'none')} />
                <span>
                  업로드만 하기
                  <span className="block text-xs text-gray-400">지금은 배치하지 않습니다 — 나중에 달력에 끌어다 놓으면 이 조건이 다시 나옵니다</span>
                </span>
              </label>
            </div>

            <div className="space-y-3 mb-4">
              {/* 행사 시작·시간·종료 — 왼쪽 고정폭 라벨 열로 세 줄 정렬 (비로그인 업로드 페이지와 동일 구조) */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  행사 일시{values.placement === 'box' ? '' : ' (선택 — 있으면 카운트다운 표시)'}
                </label>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 w-8 shrink-0">시작</span>
                  <input
                    type="datetime-local"
                    className="flex-1 min-w-0 border rounded px-2 py-1 text-sm"
                    value={values.eventStartAt}
                    onChange={(e) => onEventChange(e.target.value)}
                    onBlur={onEventStartBlur}
                  />
                </div>
                {/* 행사 시간 — 저장되지 않는 입력 보조. 행사 종료를 계산해 준다. */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-gray-500 w-8 shrink-0">시간</span>
                  <div className="flex-1 min-w-0 flex items-center gap-1">
                    {/* step=1 — step=5 는 min=1 이 기준점이라 60·120 같은 5의 배수를 stepMismatch 로 거절했다 */}
                    <input
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      className="border rounded px-2 py-1 text-sm w-20 disabled:bg-gray-100"
                      value={durationMin}
                      onChange={(e) => onDurationChange(e.target.value)}
                      disabled={!values.eventStartAt}
                      title={values.eventStartAt ? '' : '행사 시작을 먼저 입력하세요'}
                      placeholder="60"
                    />
                    <span className="text-xs text-gray-500">분</span>
                    {humanizeMinutes(Number(durationMin)) && (
                      <span className="text-[11px] text-gray-400">({humanizeMinutes(Number(durationMin))})</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-8 shrink-0">종료</span>
                  <input
                    type="datetime-local"
                    className="flex-1 min-w-0 border rounded px-2 py-1 text-sm"
                    value={values.eventEndAt}
                    onChange={(e) => onEventEndChange(e.target.value)}
                  />
                </div>
              </div>
              {eventRangeInvalid && (
                <p className="text-xs text-red-500">행사 종료가 시작보다 이릅니다 — 시각을 확인해 주세요.</p>
              )}
              {values.placement !== 'none' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">홍보 시작</label>
                    <input
                      type="datetime-local"
                      className="border rounded px-2 py-1 text-sm w-full"
                      value={values.promoStartAt}
                      onChange={(e) => { setPromoStartTouched(true); set('promoStartAt', e.target.value); }}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      {/* 빈 값은 '미지정'으로 표기 (종전 '비우면 계속 노출' 부연을 대체) */}
                      {values.placement === 'slot' && !values.promoEndAt ? '홍보 종료 (미지정)' : '홍보 종료'}
                      {!promoEndTouched && values.eventEndAt && (
                        <span className="ml-1 text-gray-400">= 행사 종료</span>
                      )}
                    </label>
                    <input
                      type="datetime-local"
                      className="border rounded px-2 py-1 text-sm w-full"
                      value={values.promoEndAt}
                      onChange={(e) => { setPromoEndTouched(true); set('promoEndAt', e.target.value); }}
                    />
                  </div>
                </div>
              )}
              {values.placement !== 'none' && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={values.isPublic}
                    onChange={(e) => set('isPublic', e.target.checked)}
                  />
                  다른 운영처(Public)에도 노출
                </label>
              )}
            </div>

            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 border rounded text-sm text-gray-600 hover:bg-gray-50">취소</button>
              <button
                onClick={submit}
                disabled={submitting || eventRangeInvalid}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50"
              >
                {submitting ? '적용 중…' : (approveMode ? '승인하고 적용' : '적용')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecipeInterview;

/** RecipeValues → 서버 recipe body (datetime-local → ISO, 빈값 = 없음). */
export function toRecipeBody(values: RecipeValues) {
  const toIso = (v: string) => (v ? new Date(v).toISOString() : null);
  return {
    event_start_at: toIso(values.eventStartAt),
    event_end_at: toIso(values.eventEndAt),
    promo_start_at: toIso(values.promoStartAt),
    promo_end_at: toIso(values.promoEndAt),
    placement: values.placement,
    is_public: values.placement === 'none' ? false : values.isPublic,
  };
}
